import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClosedMonthsDatePicker } from '@/components/impacto/ClosedMonthsDatePicker';
import { SearchableGeneratorSelect } from '@/components/dashboard/SearchableGeneratorSelect';
import { useAuthContext } from '@/components/AuthProvider';
import type { ImpactFilters as ImpactFiltersType } from '@/hooks/useImpactData';
interface ImpactFiltersProps {
  filters: ImpactFiltersType;
  onFiltersChange: (filters: Partial<ImpactFiltersType>) => void;
  onClearFilters: () => void;
  dateShortcuts: Array<{
    label: string;
    getValue: () => {
      from: Date;
      to: Date;
    };
  }>;
  closedMonths: Date[];
  establishmentOptions?: Array<{
    value: string;
    label: string;
  }>;
  wasteTypeOptions?: Array<{
    value: string;
    label: string;
  }>;
}

// Destination type options - correct values based on prototype
const DESTINATION_OPTIONS = [{
  value: 'all',
  label: 'Todas as destinações'
}, {
  value: 'Reciclagem',
  label: 'Reciclagem'
}, {
  value: 'Compostagem',
  label: 'Compostagem'
}, {
  value: 'Coprocessamento',
  label: 'Coprocessamento'
}, {
  value: 'Aterro',
  label: 'Aterro'
}];
export const ImpactFilters = ({
  filters,
  onFiltersChange,
  onClearFilters,
  dateShortcuts,
  closedMonths,
  establishmentOptions = [],
  wasteTypeOptions = []
}: ImpactFiltersProps) => {
  const { profile } = useAuthContext();
  const isInternal = profile?.is_internal === true;

  // Check if any filter is active (not default)
  // Note: destinationType filter hidden temporarily - no backend support yet
  const hasActiveFilters = filters.establishments.length > 0 || filters.wasteType !== 'all' || filters.generatorId !== null;
  return <Card className="bg-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Filtros</CardTitle>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={onClearFilters} className="text-muted-foreground hover:text-foreground gap-1.5">
              <X className="h-4 w-4" />
              Limpar filtros
            </Button>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-start gap-6">
          {/* Period Filter - Apenas meses fechados */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-muted-foreground">Período (meses fechados)</label>
            <ClosedMonthsDatePicker
              value={{
                start: filters.dateRange.from,
                end: filters.dateRange.to
              }}
              onChange={(range) => {
                if (range) {
                  onFiltersChange({
                    dateRange: {
                      from: range.start,
                      to: range.end
                    }
                  });
                }
              }}
              closedMonths={closedMonths}
              dateShortcuts={dateShortcuts}
              className="min-w-[200px]"
            />
          </div>

          {/* Destination Type Filter - HIDDEN: No backend support yet
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-muted-foreground">Tipo de Destinação</label>
            <Select value={filters.destinationType} onValueChange={value => onFiltersChange({
            destinationType: value
          })}>
              <SelectTrigger className="w-[180px] h-10">
                <SelectValue placeholder="Todas as destinações" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                {DESTINATION_OPTIONS.map(option => <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          */}

          {/* Waste Type Filter - NEW */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-muted-foreground">Tipo de Resíduo</label>
            <Select value={filters.wasteType} onValueChange={value => onFiltersChange({
            wasteType: value
          })}>
              <SelectTrigger className="w-[180px] h-10">
                <SelectValue placeholder="Todos os resíduos" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50 max-h-[300px]">
                <SelectItem value="all">Todos os resíduos</SelectItem>
                {wasteTypeOptions.map(option => <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Establishment Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-muted-foreground">Estabelecimento</label>
            <Select value={filters.establishments[0] || 'all'} onValueChange={value => onFiltersChange({
            establishments: value === 'all' ? [] : [value]
          })}>
              <SelectTrigger className="w-[180px] h-10">
                <SelectValue placeholder="Todas as unidades" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50 max-h-[300px]">
                <SelectItem value="all">Todas as unidades</SelectItem>
                {establishmentOptions.map(option => <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Generator Filter - Only for internal users */}
          {isInternal && <div className="flex flex-col gap-1.5">
              <label className="text-sm text-muted-foreground">Gerador</label>
              <SearchableGeneratorSelect value={filters.generatorId} onChange={value => onFiltersChange({
            generatorId: value
          })} placeholder="Todos os geradores" className="w-[220px]" />
            </div>}
        </div>
      </CardContent>
    </Card>;
};